import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { tagService } from '../services/tagService';
import { Tag, TAG_COLORS } from '../types/tag';

interface TagSelectorProps {
  selectedTags: string[];
  onTagsChange: (tagIds: string[]) => void;
  placeholder?: string;
  maxTags?: number;
  allowCreate?: boolean;
  className?: string;
}

const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags,
  onTagsChange,
  placeholder = "Select tags...",
  maxTags,
  allowCreate = false,
  className = ""
}) => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTags, setFilteredTags] = useState<Tag[]>([]);
  const [newTagName, setNewTagName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const createTagInputRef = useRef<HTMLInputElement>(null);

  const loadTags = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const userTags = await tagService.getUserTags(currentUser.uid);
      setTags(userTags);
    } catch (error) {
      console.error('Error loading tags:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      loadTags();
    }
  }, [currentUser, loadTags]);

  // Filter tags based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredTags(tags);
    } else {
      const filtered = tags.filter(tag =>
        tag.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !selectedTags.includes(tag.id)
      );
      setFilteredTags(filtered);
    }
  }, [tags, searchTerm, selectedTags]);


  const handleTagToggle = (tagId: string) => {
    if (selectedTags.includes(tagId)) {
      onTagsChange(selectedTags.filter(id => id !== tagId));
    } else {
      if (maxTags && selectedTags.length >= maxTags) {
        return;
      }
      onTagsChange([...selectedTags, tagId]);
    }
  };

  const handleRemoveTag = (tagId: string) => {
    onTagsChange(selectedTags.filter(id => id !== tagId));
  };

  const handleCreateTag = async () => {
    if (!currentUser || !newTagName.trim()) return;

    try {
      const validation = tagService.validateTagName(newTagName);
      if (!validation.isValid) {
        showError(validation.error || 'Invalid tag name');
        return;
      }

      const isUnique = await tagService.isTagNameUnique(currentUser.uid, newTagName);
      if (!isUnique) {
        showError('Tag name already exists');
        return;
      }

      const tagId = await tagService.createTag(currentUser.uid, {
        name: newTagName.trim(),
        color: TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)].value,
        description: ''
      });

      console.log('Tag created with ID:', tagId);
      
      // Update the selected tags immediately
      const newSelectedTags = [...selectedTags, tagId];
      onTagsChange(newSelectedTags);
      
      setNewTagName('');
      setSearchTerm('');
      setShowCreateForm(false);
      
      // Reload tags to ensure the new tag is available
      await loadTags();
      
      showSuccess('Tag created successfully');
    } catch (error) {
      console.error('Error creating tag:', error);
      showError('Failed to create tag');
    }
  };

  const getSelectedTagObjects = () => {
    return tags.filter(tag => selectedTags.includes(tag.id));
  };

  const selectedTagObjects = getSelectedTagObjects();

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Selected Tags Display */}
      <div
        className="min-h-[40px] border border-gray-300 dark:border-neutral-600 rounded-lg p-2 cursor-pointer bg-white dark:bg-neutral-700 hover:border-gray-400 dark:hover:border-neutral-500 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedTagObjects.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {selectedTagObjects.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: tag.color }}
              >
                {tag.name}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTag(tag.id);
                  }}
                  className="ml-1 hover:bg-black hover:bg-opacity-20 rounded-full p-0.5"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-500 dark:text-neutral-400">{placeholder}</span>
        )}
        
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
          <svg
            className={`w-4 h-4 text-gray-400 dark:text-neutral-500 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div 
          className="absolute z-[9999] top-full left-0 right-0 mt-1 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {/* Header with Search and Create Button */}
          <div className="p-2 border-b border-gray-200 dark:border-neutral-600">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-neutral-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-neutral-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-neutral-400"
                autoFocus
              />
              {allowCreate && (
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(true);
                    setNewTagName('');
                    setSearchTerm('');
                    setTimeout(() => {
                      createTagInputRef.current?.focus();
                    }, 0);
                  }}
                  className="px-3 py-2 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors flex items-center gap-1 whitespace-nowrap"
                  title="Create new tag"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create
                </button>
              )}
            </div>
          </div>

          {/* Tag List */}
          <div className="py-1">
            {loading ? (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-neutral-400 text-center">
                Loading tags...
              </div>
            ) : filteredTags.length > 0 ? (
              filteredTags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleTagToggle(tag.id)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-neutral-700 flex items-center space-x-2 text-gray-900 dark:text-white"
                >
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  ></div>
                  <span>{tag.name}</span>
                  {tag.description && (
                    <span className="text-gray-500 dark:text-neutral-400 text-xs">- {tag.description}</span>
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-neutral-400 text-center">
                {searchTerm ? 'No tags found' : 'No tags available'}
              </div>
            )}
          </div>

          {/* Create New Tag Form - Shown when Create button is clicked */}
          {allowCreate && showCreateForm && (
            <div className="border-t border-gray-200 dark:border-neutral-600 p-3 bg-blue-50 dark:bg-blue-900/20">
              <div className="flex items-center space-x-2">
                <div className="flex-shrink-0">
                  <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <input
                  ref={createTagInputRef}
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateTag();
                    }
                    if (e.key === 'Escape') {
                      setShowCreateForm(false);
                      setNewTagName('');
                    }
                  }}
                  placeholder="Enter tag name..."
                  className="flex-1 px-3 py-2 text-sm border border-blue-300 dark:border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-neutral-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-neutral-400"
                />
                <button
                  onClick={handleCreateTag}
                  disabled={!newTagName.trim()}
                  className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white text-sm rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewTagName('');
                  }}
                  className="px-3 py-2 text-sm text-gray-600 dark:text-neutral-400 hover:text-gray-900 dark:hover:text-white"
                  title="Cancel"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Press Enter to create the tag</p>
            </div>
          )}

          {/* Create New Tag - Shown when searching for non-existent tag */}
          {allowCreate && !showCreateForm && searchTerm && !tags.some(tag => 
            tag.name.toLowerCase() === searchTerm.toLowerCase()
          ) && (
            <div className="border-t border-gray-200 dark:border-neutral-600 p-3 bg-blue-50 dark:bg-blue-900/20">
              <div className="flex items-center space-x-2">
                <div className="flex-shrink-0">
                  <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateTag();
                    }
                  }}
                  placeholder={`Create "${searchTerm}"`}
                  className="flex-1 px-3 py-2 text-sm border border-blue-300 dark:border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-neutral-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-neutral-400"
                  autoFocus
                />
                <button
                  onClick={handleCreateTag}
                  className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white text-sm rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-200 font-medium"
                >
                  Create
                </button>
              </div>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Press Enter to create the tag</p>
            </div>
          )}
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9998]"
          onClick={() => setIsOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default TagSelector;
